/* eslint-disable no-prototype-builtins */
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import ErrorBox from 'components/ErrorBox'
import GridContainer from 'components/GlobalStyles/GridContainer'
import GridImage from 'components/GridImage'
import LoadingBox from 'components/LoadingBox'
import NoPhoto from 'components/NoPhoto'
import RoundedButton from 'components/RoundedButton'
import Checkmark from 'components/SVG/Checkmark'
import CartOutline from 'components/SVG/compound/CartOutline'
import Spacer from 'components/Spacer'
import StickyFooter from 'components/StickyFooter'

import { ImageTypeEnum, getImageUrlFromS3Url } from 'services/images/read'
import { addItemToCart } from 'services/order'
import { useCart } from 'services/swr/orders/useCart'
import { useServices } from 'services/swr/services/useServices'

import { getItemCountByListingId, hasServiceInCart } from 'utils/cart'

import * as T from 'types'

import MoreDetailModal from '../MoreDetailModal'

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  padding: 24px;
  background-color: ${props => props.theme.colors.neutral25};
`

const InfoContainer = styled.div`
  width: 100%;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-basis: 50%;
`

const ServiceTitle = styled.div`
  font-weight: 500;
  font-size: 18px;
  line-height: 25px;
  color: ${props => props.theme.colors.black};
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  flex-basis: content;
  flex-grow: 1;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }
`

interface IListingCartProps {
  listingCart?: boolean | undefined
}
const PriceInfoContainer = styled.div<IListingCartProps>`
  display: flex;
  flex-direction: ${props => (props.listingCart ? 'column' : 'row')};
`

const Price = styled.div<IListingCartProps>`
  font-weight: 800;
  font-size: 24px;
  line-height: 33px;
  color: ${props => props.theme.colors.neutral800};
  text-align: right;
  display: flex;
  align-items: center;
  margin: 5px 0;
  ${props => props.listingCart && 'justify-content: flex-end;'}
`

const ButtonContainer = styled.div<IListingCartProps>`
  ${props => props.listingCart && 'display: flex;'}
  ${props => props.listingCart && 'justify-content: space-between;'}
  width: 100%;
`

const InfoButtonWrapper = styled.div<IListingCartProps>`
  flex: 0 1 75%;
  /* set info button to fixed width if add to cart button doesn't exists */
  ${props => !props.listingCart && 'flex: 0 0 87px;'}
  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    padding: 0 61px;
  }
`

const ProductNameAndPriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const LoadServicesButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  line-height: 16px;
`

interface IProps {
  listing?: T.IListing
  max?: number
  isInDashboardFlow?: boolean
  filterNames?: string[]
  includeLoadServicesButton?: boolean
  initialDisplayNumber?: number
  incrementDisplayBy?: number
}

const Services: FunctionComponent<IProps> = ({
  listing,
  max,
  isInDashboardFlow,
  filterNames,
  includeLoadServicesButton,
  initialDisplayNumber = 6,
  incrementDisplayBy = 6,
}) => {
  // theme
  const theme = useContext(ThemeContext)

  const {
    colors: { teal500, blue500, purple, white, transparentPurpleAlpha20, yellow2 },
  } = theme

  const checkoutStyles = {
    height: 40,
    radius: 20,
    background: blue500,
    color: white,
    borderColor: blue500,
    fill: teal500,
    hoverBorderColor: yellow2,
    hoverBackground: yellow2,
  }

  const renderCartIcon = () => (
    <CartOutline
      width={16}
      height={16}
      fill={white}
    />
  )

  const renderCheckmarkIcon = () => (
    <Checkmark
      width={16}
      height={16}
      fill={purple}
    />
  )

  // swr
  const { cart, mutate } = useCart()
  const { services, error: servicesError } = useServices()

  // item state
  const getItemCount = (currentCart?: T.IOrder, listingId?: string) =>
    getItemCountByListingId(currentCart, listingId)

  const getCheckoutButtonTitle = (currentCart?: T.IOrder, listingId?: string) => {
    const count = getItemCount(currentCart, listingId)
    if (count === 0) return 'Checkout'

    return `Checkout ${count} ${count > 1 ? 'Items' : 'Item'}`
  }

  // load more services state
  const [showLoadServicesButton, setShowLoadServicesButton] = useState(includeLoadServicesButton)

  const [displayAmount, setDisplayAmount] = useState(
    initialDisplayNumber || incrementDisplayBy || max,
  )

  // modal state
  const [visibleServiceId, setVisibleServiceId] = useState('')

  // adding service id
  const [isAddingId, setIsAddingId] = useState<string>('')

  // handlers
  const handleAddItem = async (service: T.IService) => {
    if (!cart || !listing) return // setError()

    setIsAddingId(service._id)

    const updatedCart = await addItemToCart(cart, listing._id, service._id, service, 1)
    if (!updatedCart) return // setError()

    mutate(updatedCart)
    setIsAddingId('')
    setVisibleServiceId('')
  }

  const handleShowServices = () => {
    if (includeLoadServicesButton && displayAmount) {
      let amount = displayAmount + incrementDisplayBy
      if (max && amount > max) amount = max

      setDisplayAmount(amount)
    }
  }

  useEffect(() => {
    if (displayAmount && services) {
      const currentServices = filterNames
        ? services.filter(service => filterNames.includes(service.name))
        : services

      if (currentServices.length <= displayAmount) setShowLoadServicesButton(false)
    }
  }, [displayAmount, services])

  if (servicesError) return <ErrorBox error="There was a problem loading services" />

  if (!services) return <LoadingBox height={300} />

  const sortedServices = services.sort((serviceA, serviceB) => {
    if (serviceA.hasOwnProperty('order') && !serviceB.hasOwnProperty('order')) return -1

    if (!serviceA.hasOwnProperty('order') && serviceB.hasOwnProperty('order')) return 1

    if (!serviceA.hasOwnProperty('order') && !serviceB.hasOwnProperty('order')) return 0

    return (serviceA?.order ?? 0) - (serviceB?.order ?? 0)
  })

  const getServices = () => {
    const currentServices = filterNames
      ? services.filter(service => filterNames.includes(service.name))
      : sortedServices

    if (includeLoadServicesButton) {
      return currentServices.slice(0, displayAmount || currentServices.length)
    }

    return currentServices.slice(0, max || currentServices.length)
  }

  return (
    <>
      <GridContainer
        tabletCols={isInDashboardFlow ? 2 : 3}
        desktopCols={3}
        largeDesktopCols={3}
        tabletGridGap={24}
        desktopGridGap={24}
        largeDesktopGridGap={24}
        justifyContent="center"
      >
        {getServices().map(service => {
          const handleClickMoreInfo = () => setVisibleServiceId(service._id)
          const handleClickAddItem = () => handleAddItem(service)
          const photoSrc = getImageUrlFromS3Url(service.photo, ImageTypeEnum.Service)
          return (
            <Container key={service._id}>
              {photoSrc
                ? (
                  <GridImage
                    src={photoSrc}
                    alt={service.name}
                    height={173}
                    borderRadius={12}
                  />
                )
                : (
                  <NoPhoto
                    height={173}
                    borderRadius={12}
                  />
                )}
              <Spacer size={22} />
              <InfoContainer>
                <ProductNameAndPriceContainer>
                  <ServiceTitle>{service.name}</ServiceTitle>
                  <Price listingCart={!!listing}>{`$${service.price}`}</Price>
                </ProductNameAndPriceContainer>
                <PriceInfoContainer listingCart={!!listing}>
                  <ButtonContainer listingCart={!!listing}>
                    {listing
                      && cart
                      && (hasServiceInCart(listing._id, cart, service._id)
                        ? (
                          <RoundedButton
                            dataTestId={`${service.name} Added`}
                            color={purple}
                            background={transparentPurpleAlpha20}
                            height={36}
                            radius={3}
                            iconPadding={3}
                            text="Added"
                            renderLeftIcon={renderCheckmarkIcon}
                            compactText
                            noUppercase
                            light
                            fullWidth
                          />
                        )
                        : (
                          <RoundedButton
                            dataTestId={`${service.name} Add`}
                            color={white}
                            borderWidth={1}
                            borderColor={blue500}
                            background={blue500}
                            height={36}
                            radius={3}
                            iconPadding={3}
                            text="Add"
                            renderLeftIcon={renderCartIcon}
                            onClick={handleClickAddItem}
                            loading={!!isAddingId && isAddingId === service._id}
                            compactText
                            noUppercase
                            light
                            fullWidth
                          />
                        ))}
                    {listing && <Spacer size={10} />}
                    <InfoButtonWrapper listingCart={!!listing}>
                      <RoundedButton
                        dataTestId={`${service.name} Info`}
                        background={white}
                        color={blue500}
                        borderWidth={1}
                        borderColor={blue500}
                        height={44}
                        radius={50}
                        lineHeight={16}
                        fontSize={12}
                        text="More Info"
                        onClick={handleClickMoreInfo}
                        fullWidth
                        bold
                      />
                    </InfoButtonWrapper>
                  </ButtonContainer>
                </PriceInfoContainer>
                <MoreDetailModal
                  isServiceAdded={
                    listing && cart && hasServiceInCart(listing._id, cart, service._id)
                  }
                  service={service}
                  visible={visibleServiceId === service._id}
                  addToCart={!!listing}
                  setVisible={setVisibleServiceId}
                  handleAddItem={handleAddItem}
                />
              </InfoContainer>
            </Container>
          )
        })}
      </GridContainer>
      {includeLoadServicesButton && showLoadServicesButton && (
        <>
          <Spacer size={36} />
          <LoadServicesButtonContainer>
            <RoundedButton
              width={230}
              height={44}
              radius={50}
              fontSize={12}
              lineHeight={16}
              background={blue500}
              color={white}
              text="Show More Services"
              bold
              onClick={handleShowServices}
            />
          </LoadServicesButtonContainer>
        </>
      )}
      {isInDashboardFlow && listing && getItemCount(cart, listing._id) > 0 && (
        <>
          <Spacer size={20} />
          <StickyFooter showDropShadow={false}>
            <RoundedButton
              {...checkoutStyles}
              text={getCheckoutButtonTitle(cart, listing._id)}
              href={`/listing/${listing._id}/dashboard/checkout`}
            />
          </StickyFooter>
        </>
      )}
    </>
  )
}

export default Services
